import ThemedSelect from "../shared/ThemedSelect.jsx";
import { useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Card, Form, Modal, Spinner } from "react-bootstrap";
import DropZone from "../shared/DropZone.jsx";
import "../../styles/dropzone.css";
import { FaUpload, FaTrash, FaDownload, FaFileAlt, FaFilePdf, FaFileWord, FaFileExcel, FaFilePowerpoint, FaFileArchive, FaFileCsv, FaFileVideo, FaEnvelope, FaImage, FaChevronLeft, FaChevronRight, FaTimes, FaCloudDownloadAlt, FaLink } from "react-icons/fa";
import TabHeader from "./TabHeader.jsx";
import FileCard, { Lightbox } from "./FileCard.jsx";
import ZipAllPhotos from "../../experiments/photo-helix/ZipAllPhotos.jsx"; // experiment, see its README
import {
  listClaimFiles,
  uploadClaimFiles,
  addClaimLink,
  deleteClaimFile,
  renameClaimFile,
  fileContentUrl,
  getCurrentUser,
  backfillClaimFilesFromGps,
} from "../../services/apiClient.js";

// Files tab — a 1-to-1 port of the legacy LogPhotoPage / PhotoViewByType: attachments
// grouped by file type into sections, each with a "show all / limit" toggle (jobs have
// hundreds of photos), and click-a-photo → full-screen viewer (legacy PhotoView.aspx).
// Bytes come from the storage backend via a content URL that 404s gracefully when
// absent (legacy imports, wiped demo store) → a clean placeholder instead of a crash.

// Section order matches legacy PhotoViewByType.
const TYPE_ORDER = [
  "Request", "Insurer Assessor", "Inspector", "Contractor",
  "Contractor H&S", "Contractor Photos", "Client", "Other", "Progress Entry",
];
const COLLAPSED_LIMIT = 12;

function ClaimFilesTab({ claimId }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  // Add Link (Ruby, 2026-08-24): url + optional label, filed under the same
  // type dropdown as uploads so links sit in the section they belong to.
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkForm, setLinkForm] = useState({ url: "", name: "" });
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState({}); // group -> show all
  const [lightboxId, setLightboxId] = useState(null);
  const [uploadType, setUploadType] = useState("Other"); // which section new files land in
  // Off by default so the counts match 1.0 and each file has one home. On, the
  // other collections appear under their own headings for a single place to look.
  const [showAllSources, setShowAllSources] = useState(false);
  const [prefix, setPrefix] = useState("");
  const [me, setMe] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const inputRef = useRef(null);

  function load() {
    setLoading(true);
    return listClaimFiles(claimId)
      .then(setFiles)
      .catch(() => setError("Unable to load files."))
      .finally(() => setLoading(false));
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId]);
  useEffect(() => {
    getCurrentUser().then(setMe).catch(() => {});
  }, []);

  // WHAT BELONGS ON THIS TAB, and why it is not simply "everything".
  //
  // A claim's files live in several collections, and 1.0 shows each where it
  // belongs: job attachments here, inspection files on the Inspection page, QCC
  // on QCC, a contractor invoice's document on that invoice. Progress-note
  // attachments are the exception — 1.0 keeps those in the SAME table as job
  // attachments, so they have always shown here, and they still do.
  //
  // Showing everything unconditionally is what went wrong on 2026-08-06: files
  // from two different collections both carry file_type "Inspector", so an
  // inspection's 80 files and the job's 72 added up to a silent 152 under one
  // heading, and nobody could tell which was which.
  //
  // So: the job collection by default, and a toggle to bring the others in — but
  // under their OWN headings, never merged into the type groups. The data is all
  // fetched either way; this is purely how it is grouped.
  const JOB_CATEGORIES = ["claim_image", "progress"];
  const OTHER_SOURCES = [
    { key: "inspection", label: "Inspection files", categories: ["inspection_photo", "inspection_doc"] },
    { key: "qcc", label: "QCC files", categories: ["qcc", "qcc_for_inspector"] },
    { key: "invoice", label: "Contractor invoice documents", categories: ["contractor_invoice"] },
  ];

  const jobFiles = useMemo(
    () => files.filter((f) => JOB_CATEGORIES.includes(f.category)),
    [files]
  );
  const otherCount = files.length - jobFiles.length;

  const groups = useMemo(() => {
    const byType = new Map();
    for (const f of jobFiles) {
      const key = TYPE_ORDER.includes(f.file_type) ? f.file_type : "Other";
      if (!byType.has(key)) byType.set(key, []);
      byType.get(key).push(f);
    }
    const out = TYPE_ORDER.filter((t) => byType.has(t)).map((t) => ({ type: t, files: byType.get(t) }));
    if (!showAllSources) return out;
    for (const src of OTHER_SOURCES) {
      const found = files.filter((f) => src.categories.includes(f.category));
      if (found.length) out.push({ type: src.label, files: found, borrowed: true });
    }
    return out;
  }, [files, jobFiles, showAllSources]);

  // Flat list of images (across groups, display order) for lightbox navigation.
  const images = useMemo(() => groups.flatMap((g) => g.files.filter((f) => f.is_image)), [groups]);
  const lightboxIndex = images.findIndex((f) => f.id === lightboxId);

  async function onPick(e) {
    const picked = [...(e.target.files || [])];
    e.target.value = "";
    await sendFiles(picked);
  }
  // Dropped files take the SAME path as picked ones — same type dropdown, same
  // prefix, same refresh — so the two never drift apart.
  async function sendFiles(picked) {
    if (picked.length === 0) return;
    setUploading(true);
    setError("");
    try {
      await uploadClaimFiles(claimId, picked, { fileType: uploadType, prefix });
      setPrefix("");
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Upload failed.");
    } finally {
      setUploading(false);
    }
  }
  async function remove(file) {
    if (!window.confirm(`Delete "${file.original_filename}"?`)) return;
    try {
      await deleteClaimFile(claimId, file.id);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Delete failed.");
    }
  }
  // Errors stay in the card (FileCard shows them beside the input), so no catch.
  async function rename(file, name) {
    await renameClaimFile(claimId, file.id, name);
    await load();
  }
  // Admin utility: pull the bytes for metadata-only files from GPSWebHelper/Base4.
  async function backfillFromGps() {
    setBackfilling(true);
    setError("");
    try {
      const r = await backfillClaimFilesFromGps(claimId);
      await load();
      const bits = [`${r.stored} of ${r.requested} restored`];
      if (r.failed) bits.push(`${r.failed} failed`);
      if (r.missing?.length) bits.push(`${r.missing.length} not found in Base4`);
      setError(""); // clear any prior error; show a transient success note
      window.alert(`GPS backfill: ${bits.join(", ")}.`);
    } catch (err) {
      setError(err.response?.data?.error || "GPS backfill failed.");
    } finally {
      setBackfilling(false);
    }
  }

  async function saveLink() {
    setError("");
    try {
      await addClaimLink(claimId, { url: linkForm.url, name: linkForm.name, category: uploadType });
      setLinkOpen(false);
      setLinkForm({ url: "", name: "" });
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to add the link.");
    }
  }

  const imageCount = images.length;
  const missingBytes = files.filter((f) => !f.has_bytes).length;
  const canBackfill = !!me?.is_admin && missingBytes > 0;

  return (
    <>
      <TabHeader
        title="Files"
        help="files"
        sub={
          !loading
            ? `${jobFiles.length} file${jobFiles.length === 1 ? "" : "s"} · ${imageCount} image${imageCount === 1 ? "" : "s"}` +
              (otherCount && !showAllSources ? ` · ${otherCount} more on other tabs` : "")
            : null
        }
        actions={
          <>
            {canBackfill ? (
              <Button variant="outline-primary" size="sm" onClick={backfillFromGps} disabled={backfilling} data-hint="Admin: pull the original file bytes from GPS/Base4 for files imported as metadata only">
                {backfilling ? <><Spinner size="sm" animation="border" className="me-1" /> Fetching…</> : <><FaCloudDownloadAlt className="me-1" /> Get {missingBytes} from GPS</>}
              </Button>
            ) : null}
            {/* EXPERIMENT — renders nothing unless you are the system owner (or a
                named demo guest), and checks that itself, so nothing here has to.
                This line and one `data-ph-tile` attribute below are all the tab
                knows about it; delete both and src/experiments/photo-helix/ and
                the ANIMATION is gone. The zip endpoint behind it is ordinary app
                code and stays. See that folder's README.
                It sits BEFORE the upload controls with a gap: Zip All takes files
                OUT and the three controls after it put files IN, so sitting among
                them read as part of the upload flow. */}
            {otherCount ? (
              <Form.Check
                type="switch"
                id="files-show-all-sources"
                className="me-3"
                label={`Include inspection, QCC and invoice files (${otherCount})`}
                checked={showAllSources}
                onChange={(e) => setShowAllSources(e.target.checked)}
                data-hint="These live on their own tabs. Shown here under their own headings, they are not counted as job attachments."
              />
            ) : null}
            {/* The picker's sections are the tab's OWN headings, not a second
                grouping worked out from the rows. With the switch on, the rows
                alone would put an inspection's files and the job's under one
                "Inspector" heading again — the exact merge the headings above
                exist to prevent — so Pick would offer a section that means two
                different collections. */}
            <ZipAllPhotos
              claimId={claimId}
              files={showAllSources ? files : jobFiles}
              groups={groups.map((g) => ({ name: g.type, ids: g.files.map((f) => f.id) }))}
            />
            <span className="me-3" aria-hidden="true" />
            <ThemedSelect size="sm" style={{ width: 175 }} value={uploadType} onChange={(e) => setUploadType(e.target.value)} data-hint="File type new uploads are filed under" disabled={uploading}>
              {TYPE_ORDER.map((t) => <option key={t} value={t}>{t}</option>)}
            </ThemedSelect>
            <Form.Control size="sm" style={{ width: 145 }} placeholder="Prefix (optional)" value={prefix} onChange={(e) => setPrefix(e.target.value)} disabled={uploading} />
            <input ref={inputRef} type="file" multiple hidden onChange={onPick} />
            <Button size="sm" onClick={() => inputRef.current?.click()} disabled={uploading}>
              {uploading ? <><Spinner size="sm" animation="border" className="me-1" /> Uploading…</> : <><FaUpload className="me-1" /> Upload</>}
            </Button>
            <Button size="sm" variant="outline-primary" onClick={() => setLinkOpen(true)} disabled={uploading}
              data-hint="Attach a web address — a YouTube walkthrough, a shared folder — filed under the selected type">
              <FaLink className="me-1" /> Add Link
            </Button>
            {/* Dragging has always worked — the whole list below is the drop
                target — but the only thing that SAID so was the empty state, so
                on a tab with files in it the feature was invisible. Karen
                Sweatman went looking for it on the Upload button (2026-08-12),
                which is exactly where someone would look, so the sentence goes
                here rather than in a corner. Hidden mid-upload, when the button
                beside it is already saying what is happening. */}
            {uploading ? null : (
              <span className="text-muted small ms-1 d-none d-lg-inline">
                or drop files anywhere below
              </span>
            )}
          </>
        }
      />

      <Modal show={linkOpen} onHide={() => setLinkOpen(false)} size="sm">
        <Modal.Header closeButton><Modal.Title className="h6">Add a link</Modal.Title></Modal.Header>
        <Modal.Body className="py-2">
          <Form.Label className="mb-1 small text-muted">Web address</Form.Label>
          <Form.Control size="sm" autoFocus placeholder="https://…" value={linkForm.url}
            onChange={(e) => setLinkForm((f) => ({ ...f, url: e.target.value }))}
            onKeyDown={(e) => { if (e.key === "Enter" && linkForm.url.trim()) saveLink(); }} />
          <Form.Label className="mb-1 mt-2 small text-muted">Label (optional)</Form.Label>
          <Form.Control size="sm" placeholder="e.g. Site walkthrough video" value={linkForm.name}
            onChange={(e) => setLinkForm((f) => ({ ...f, name: e.target.value }))}
            onKeyDown={(e) => { if (e.key === "Enter" && linkForm.url.trim()) saveLink(); }} />
          <div className="text-muted small mt-2">Filed under {uploadType} (the type dropdown).</div>
        </Modal.Body>
        <Modal.Footer className="py-2">
          <Button size="sm" variant="outline-secondary" onClick={() => setLinkOpen(false)}>Cancel</Button>
          <Button size="sm" onClick={saveLink} disabled={!linkForm.url.trim()}>Add</Button>
        </Modal.Footer>
      </Modal>

      {error ? <div className="alert alert-danger">{error}</div> : null}

      {/* The whole list is the drop target, so files can be dragged anywhere on
          the tab rather than onto one small button. Dropped files use the type
          dropdown and prefix above, exactly as picked ones do. */}
      <DropZone
        className="py-1"
        disabled={uploading}
        label={`Drop files here to upload as "${uploadType}"`}
        onFiles={sendFiles}
      >
      {loading ? (
        <div className="text-center text-muted py-5"><Spinner animation="border" size="sm" /> Loading…</div>
      ) : files.length === 0 ? (
        <div className="text-center text-muted py-5">No files. Drag them here, or use <strong>Upload</strong>.</div>
      ) : (
        groups.map((g) => {
          const showAll = expanded[g.type];
          const shown = showAll ? g.files : g.files.slice(0, COLLAPSED_LIMIT);
          return (
            <section key={g.type} className="mb-4">
              <div className="d-flex align-items-center gap-2 mb-2 border-bottom pb-1">
                <h5 className="mb-0">{g.type}</h5>
                {/* Say plainly that a borrowed section lives elsewhere, so nobody
                    reads it as another pile of job attachments — which is the
                    confusion that started this. */}
                {g.borrowed ? (
                  <span className="badge bg-body-tertiary text-body-secondary border fw-normal">shown from its own tab</span>
                ) : null}
                <Badge bg="secondary" pill>{g.files.length}</Badge>
                {g.files.length > COLLAPSED_LIMIT ? (
                  <Button variant="link" size="sm" className="p-0 ms-auto" onClick={() => setExpanded((s) => ({ ...s, [g.type]: !showAll }))}>
                    {showAll ? "Show fewer" : `Show all ${g.files.length}`}
                  </Button>
                ) : null}
              </div>
              <div className="d-grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))" }}>
                {shown.map((f) => (
                  <FileCard key={f.id} file={f} onOpen={(file) => setLightboxId(file.id)} onDelete={remove} onRename={rename} />
                ))}
              </div>
            </section>
          );
        })
      )}
      </DropZone>

      {lightboxIndex >= 0 ? (
        <Lightbox
          images={images}
          index={lightboxIndex}
          onClose={() => setLightboxId(null)}
          onNav={(d) => {
            const next = images[lightboxIndex + d];
            if (next) setLightboxId(next.id);
          }}
        />
      ) : null}
    </>
  );
}

export default ClaimFilesTab;
